import { Badge } from "./Badge";
import type { GhlStatus, RuntimeStatus, SiteInventory } from "../types";

interface HeroProps {
  system: RuntimeStatus | undefined;
  ghl: GhlStatus | undefined;
  sites: SiteInventory | undefined;
  loading: boolean;
  errored: boolean;
}

function computeHero(
  system: RuntimeStatus | undefined,
  ghl: GhlStatus | undefined,
  sites: SiteInventory | undefined,
): { level: "ok" | "warn" | "bad" | "unknown"; headline: string; sub: string; hint: string | null } {
  const systemOk = Boolean(system?.ok && system?.database?.ok);
  const oauthOk = Boolean(ghl?.oauth_configured);
  const siteList = sites?.sites ?? [];
  const oauthSites = siteList.filter((s) => s.auth_mode === "oauth_managed").length;
  const fallback = siteList.filter((s) => s.auth_mode === "fallback_pit").length;
  const writeReadySites = ghl?.resolver?.write_ready_sites ?? siteList
    .filter((s) => s.resolver?.write_ready)
    .length;
  const oauthWriteReadySites = ghl?.resolver?.oauth_write_ready_sites ?? siteList
    .filter((s) => s.auth_mode === "oauth_managed" && s.resolver?.write_ready)
    .length;

  if (!system) {
    return {
      level: "unknown",
      headline: "Loading status…",
      sub: "Reading /api/status and /api/ghl/status.",
      hint: null,
    };
  }
  if (!systemOk) {
    return {
      level: "bad",
      headline: "System is not healthy.",
      sub: "Database or service is unhealthy. See the System panel.",
      hint: "Check the System panel below for the failing component.",
    };
  }
  if (siteList.length === 0) {
    return {
      level: "warn",
      headline: "No sites configured yet.",
      sub: "Connect a GHL location to start writing leads.",
      hint: "Run `make apply-ghl-site-manifest` or complete a GHL install to add a site.",
    };
  }
  if (oauthSites > 0 && fallback === 0 && oauthWriteReadySites > 0) {
    return {
      level: "ok",
      headline: `OAuth write lane ready on ${oauthWriteReadySites} site${oauthWriteReadySites === 1 ? "" : "s"}.`,
      sub: "Token lifecycle is server-managed. No operator token paste required.",
      hint: null,
    };
  }
  if (oauthSites > 0 && fallback === 0) {
    return {
      level: "warn",
      headline: `OAuth lane visible on ${oauthSites} site${oauthSites === 1 ? "" : "s"}.`,
      sub: "Agency auth metadata is present, but downstream write readiness is not proven.",
      hint: "Run the resolver diagnostic and keep fallback available until the location-token gate passes.",
    };
  }
  if (oauthSites === 0 && fallback > 0) {
    return {
      level: writeReadySites > 0 ? "ok" : "warn",
      headline: `Fallback PIT lane active on ${fallback} site${fallback === 1 ? "" : "s"}.`,
      sub: oauthOk
        ? "OAuth client is configured."
        : "OAuth client is not configured.",
      hint: oauthOk
        ? "Complete a marketplace install to move sites onto the zero-touch lane."
        : "Configure GHL OAuth client id and secret to unlock the zero-touch lane.",
    };
  }
  if (oauthSites > 0 && fallback > 0) {
    return {
      level: "warn",
      headline: `Mixed lanes: ${oauthSites} OAuth, ${fallback} fallback.`,
      sub: `${writeReadySites} site${writeReadySites === 1 ? "" : "s"} are ready for the current shared workflow write path.`,
      hint: "Migrate fallback rows only after verifying OAuth location-token writes per location.",
    };
  }
  return {
    level: "warn",
    headline: "Site lane state is unclear.",
    sub: "Inspect the Sites panel for per-site auth_mode and auth_status.",
    hint: null,
  };
}

export function Hero({ system, ghl, sites, loading, errored }: HeroProps) {
  if (errored) {
    return (
      <section className="hero hero-bad" aria-label="Overall system summary">
        <div className="hero-status">
          <span className="hero-badge bad">Down</span>
          <div>
            <p className="hero-headline">Could not reach the console backend.</p>
            <p className="hero-sub">See the panel-level errors below for the failing endpoint.</p>
          </div>
        </div>
      </section>
    );
  }
  const hero = computeHero(system, ghl, sites);
  return (
    <section className={`hero hero-${hero.level}`} aria-label="Overall system summary">
      <div className="hero-status">
        <span className={`hero-badge ${hero.level}`}>
          {loading
            ? "Loading"
            : hero.level === "ok"
              ? "Healthy"
              : hero.level === "warn"
                ? "Review"
                : hero.level === "bad"
                  ? "Down"
                  : "…"}
        </span>
        <div>
          <p className="hero-headline">{hero.headline}</p>
          <p className="hero-sub">{hero.sub}</p>
        </div>
      </div>
      {hero.hint ? <p className="hero-hint">{hero.hint}</p> : null}
      {loading && !system ? (
        <p className="hero-sub muted-meta" style={{ marginTop: 6 }}>
          <Badge tone="muted">Loading</Badge>
        </p>
      ) : null}
    </section>
  );
}
